// src/ui/LinkCard.jsx — the Open Graph card, in the bubble and above the composer.
//
// A port of the app's LinkPreviewCard: eyebrow (site name, uppercase, blue), title,
// description, then a strip of up to three images across the bottom. The favicon and
// the image strip are mutually exclusive — a page with images never shows its favicon,
// a page without them shows a 50px favicon square to the left of the text. That's the
// app's rule and it's a good one: it keeps a card with no artwork from looking empty.
//
// The app hard-codes this card light (#F8F9FA on any theme, in or out bubble). Here it
// follows the theme instead — a white slab on a dark chat is a hole punched in the
// window, and unlike the phone this client has a real dark mode to respect.

import React from 'react';
import { X } from './icons';
import { openLink } from '../services/links';

function Img({ src, className }) {
  const [dead, setDead] = React.useState(false);
  React.useEffect(() => { setDead(false); }, [src]);
  if (!src || dead) return null;
  return <img src={src} alt="" className={className} onError={() => setDead(true)} loading="lazy" />;
}

export default function LinkCard({ meta, flatTop, onClose, compact }) {
  if (!meta || (!meta.title && !meta.url)) return null;
  const images = (meta.images || []).slice(0, 3);
  const favicon = !images.length ? (meta.favicons || [])[0] : null;
  let host = '';
  try { host = new URL(meta.url).hostname.replace(/^www\./, ''); } catch {}

  const open = (e) => {
    if (onClose) return;               // the composer strip isn't a link, it's a preview
    e.stopPropagation();
    openLink(meta.url);
  };

  return (
    <div
      className={`lc ${flatTop ? 'flat' : ''} ${onClose ? 'strip' : ''} ${compact ? 'compact' : ''}`}
      onClick={open}
      role={onClose ? undefined : 'link'}
      title={onClose ? undefined : meta.url}
    >
      <div className="lc-head">
        {favicon && <span className="lc-fav"><Img src={favicon} /></span>}
        <div className="lc-txt">
          <div className="lc-eyebrow">{(meta.siteName || host || 'Link').toUpperCase()}</div>
          <div className="lc-title">{meta.title || meta.url}</div>
          {meta.description && <div className="lc-desc">{meta.description}</div>}
        </div>
      </div>

      {!!images.length && (
        <div className={`lc-strip n${images.length}`}>
          {images.map((u, i) => <span className="lc-tile" key={u + i}><Img src={u} /></span>)}
        </div>
      )}

      {onClose && (
        <button
          className="lc-x"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          title="Don't send a preview"
        >
          <X width={14} height={14} />
        </button>
      )}
    </div>
  );
}
